import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, Clock, Sparkles, GraduationCap } from "lucide-react";
import client from "../../api/client";

// High-impact fallback scholarship data to ensure we always show active funding opportunities
const MOCK_SCHOLARSHIPS = [
  {
    id: "sch-1",
    slug: "e1x-innovate-scholarship",
    title: "E1X Tech Innovate Scholarship Scheme",
    description: "Get up to 75% tuition funding for E1X Academy live cohort programs. Designed for passionate beginners and career switchers entering Software Engineering, Data Analytics, and Product Management. Includes 1-on-1 mentorship and career placement assistance.",
    deadline: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
    onboarding_date: "August 1, 2026",
    apply_url: "https://app.e1xacademy.net/innovate-hub-schorlaship",
    cover_image_url: "/assets/AI.png"
  }
];

function stripHtml(html) {
  return (html || "").replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
}

function excerpt(text, maxWords = 45) {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) return text;
  return words.slice(0, maxWords).join(" ") + "…";
}

function formatDeadline(dateStr) {
  if (!dateStr) return "Rolling Basis";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

// Featured single card component
function FeaturedCard({ item }) {
  const href = item.apply_url || `/scholarships/${item.slug}`;
  const description = excerpt(stripHtml(item.description));

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="bg-white border border-slate-100 rounded-[2.5rem] shadow-xl overflow-hidden grid grid-cols-1 md:grid-cols-12"
    >
      {/* Image Panel */}
      {item.cover_image_url && (
        <div className="md:col-span-5 relative min-h-[260px] md:min-h-full bg-slate-900 overflow-hidden">
          <img
            src={item.cover_image_url}
            alt={item.title}
            className="w-full h-full object-cover absolute inset-0 opacity-75 hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-[#d15424]/40 to-transparent" />
        </div>
      )}

      {/* Content Panel */}
      <div className={`p-8 md:p-12 flex flex-col justify-between ${item.cover_image_url ? "md:col-span-7" : "md:col-span-12"}`}>
        <div>
          {/* Badge */}
          <div className="inline-flex items-center gap-1.5 bg-orange-50 border border-orange-100 rounded-full px-3.5 py-1 mb-5">
            <GraduationCap size={13} className="text-[#d15424]" />
            <span className="text-[10px] font-bold text-[#d15424] uppercase tracking-wider">Featured Scholarship</span>
          </div>

          {/* Title */}
          <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-slate-900 leading-tight mb-4">
            {item.title}
          </h3>

          {/* Description */}
          <p className="text-slate-500 text-sm leading-relaxed mb-6">
            {description}
          </p>

          {/* Dates */}
          <div className="flex flex-wrap gap-3 mb-8">
            {item.deadline && (
              <div className="flex items-center gap-2 bg-slate-50 border border-slate-100 rounded-xl px-4 py-2">
                <Calendar size={14} className="text-slate-400" />
                <span className="text-xs text-slate-600 font-semibold">Deadline: {formatDeadline(item.deadline)}</span>
              </div>
            )}
            {item.onboarding_date && (
              <div className="flex items-center gap-2 bg-slate-50 border border-slate-100 rounded-xl px-4 py-2">
                <Clock size={14} className="text-slate-400" />
                <span className="text-xs text-slate-600 font-semibold">Cohort Starts: {item.onboarding_date}</span>
              </div>
            )}
          </div>
        </div>

        {/* Action Button */}
        <div>
          <a
            href={href}
            target={item.apply_url ? "_blank" : "_self"}
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#d15424] text-white font-bold text-sm px-8 py-3.5 rounded-full shadow-lg hover:shadow-orange-500/20 hover:bg-[#0b0f19] active:scale-[0.98] transition-all duration-300"
          >
            Apply Now
            <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </motion.div>
  );
}

// Multi-scholarship Grid Card
function GridCard({ item, index }) {
  const href = item.apply_url || `/scholarships/${item.slug}`;
  const description = excerpt(stripHtml(item.description));

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.12, duration: 0.6 }}
      className="rounded-[2.25rem] bg-white border border-slate-100 shadow-md hover:shadow-2xl hover:border-slate-200/50 hover:-translate-y-1 transition-all duration-500 flex flex-col overflow-hidden h-full"
    >
      {item.cover_image_url && (
        <div className="h-44 relative bg-slate-900 overflow-hidden">
          <img
            src={item.cover_image_url}
            alt={item.title}
            className="w-full h-full object-cover opacity-80"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/20 to-transparent" />
        </div>
      )}

      <div className="p-8 flex flex-col flex-grow">
        <div className="w-10 h-10 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center mb-5">
          <GraduationCap size={18} className="text-[#d15424]" />
        </div>

        <h4 className="font-bold text-slate-800 text-base lg:text-[17px] leading-snug mb-3 group-hover:text-[#d15424] transition-colors line-clamp-2">
          {item.title}
        </h4>
        
        <p className="text-slate-500 text-xs leading-relaxed mb-6 line-clamp-3">
          {description}
        </p>

        <div className="mt-auto border-t border-slate-50 pt-4 flex justify-between items-center">
          <div>
            <span className="block text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Deadline</span>
            <span className="text-xs font-bold text-slate-700">{formatDeadline(item.deadline)}</span>
          </div>
          <a
            href={href}
            target={item.apply_url ? "_blank" : "_self"}
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[#d15424] font-bold text-xs hover:gap-1.5 transition-all"
          >
            Apply <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </motion.div>
  );
}

export default function ScholarshipSection() {
  const [scholarships, setScholarships] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client
      .get("/scholarships")
      .then((res) => {
        const list = res.data?.data || res.data?.scholarships || res.data || [];
        if (Array.isArray(list) && list.length > 0) {
          setScholarships(list);
        } else {
          setScholarships(MOCK_SCHOLARSHIPS);
        }
      })
      .catch(() => {
        setScholarships(MOCK_SCHOLARSHIPS);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <section className="bg-white py-20 px-6 lg:px-8 border-b border-slate-50 relative overflow-hidden">
      {/* Background soft glow */}
      <div className="absolute top-0 right-0 w-80 h-80 bg-orange-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex flex-col lg:flex-row gap-12 items-start">
          
          {/* Left Text Column */}
          <div className="w-full lg:w-[35%] lg:sticky lg:top-32">
            <div className="inline-flex items-center gap-1.5 bg-orange-50 border border-orange-100 rounded-full px-3.5 py-1 mb-6">
              <Sparkles size={13} className="text-[#d15424]" />
              <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Financial Funding</span>
            </div>

            <h2 className="text-slate-900 text-3xl md:text-4xl lg:text-[40px] leading-tight font-black mb-4 tracking-tight">
              Invest in your <span className="text-[#d15424]">future.</span>
            </h2>
            
            <p className="text-slate-500 text-sm leading-relaxed mb-6">
              We believe financial barriers should not stop ambitious minds. Apply for our scholarship opportunities tailored to kickstart your tech journey and ease your enrollment.
            </p>
          </div>

          {/* Cards Column */}
          <div className="w-full lg:w-[65%]">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[1, 2].map((i) => (
                  <div key={i} className="animate-pulse bg-white border border-slate-100 rounded-3xl p-6 h-64 flex flex-col justify-between shadow-sm">
                    <div className="h-6 bg-slate-100 rounded w-1/3" />
                    <div className="h-4 bg-slate-100 rounded w-3/4" />
                    <div className="h-4 bg-slate-100 rounded w-1/2" />
                    <div className="h-10 bg-slate-100 rounded w-1/3" />
                  </div>
                ))}
              </div>
            ) : scholarships.length === 0 ? (
              <p className="text-slate-400 font-semibold text-sm">No active scholarships at the moment. Check back soon.</p>
            ) : scholarships.length === 1 ? (
              <FeaturedCard item={scholarships[0]} />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {scholarships.map((item, i) => (
                  <GridCard key={item.id || i} item={item} index={i} />
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </section>
  );
}
